import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';
import { MediaViewerService } from '@fi-shared/media-viewer/media-viewer.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { FormQuestionType } from '@fi-shared/fi-form-shared/fi-form-enums';
import { IFormReportAnswer, IFormReportAnswerSet, IFormReportSubmission, IFormReportView } from '../../fi-form-report';
import { FiImageSize, FileType } from '@fi-shared/types';
import { IFormQuestion, IFormQuestionPresetOption } from '@fi-shared/fi-form-shared/fi-form-shared';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { Observable } from 'rxjs';
import { FiFormConditionHelperService } from '@fi-shared/fi-form-shared/fi-form-condition-helper.service';
import { UserService } from '@fi-app/user/user.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';

@Component({
    selector: 'fi-form-report-activity-user-report',
    templateUrl: './fi-form-report-activity-user-report.component.html',
    styleUrls: [
        './fi-form-report-activity-user-report.component.scss',
        '../fi-form-report-activity-list.component.scss',
        '../../fi-form-report.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class FiFormReportActivityUserReportComponent implements OnInit, OnDestroy {
    @Input() submissionId: number; // (user Id who created submission)
    @Input() answerSetId: number; // (selected answer set for report)
    @Input() formReport: IFormReportView;
    @Input() isCompleteReport: boolean;
    @Output() back = new EventEmitter();
    answersArray: IFormReportAnswer[];

    private _submission: IFormReportSubmission;
    get submission() {
        if (!this._submission) {
            this._submission = this.formReport.Submissions.find((submission) => submission.Id == this.submissionId);
        }
        return this._submission;
    }

    private _answerSet: IFormReportAnswerSet;
    get answerSet() {
        if (!this._answerSet) {
            this._answerSet = this.submission.AnswerSets.find((answerSet) => answerSet.Id == this.answerSetId);
        }
        return this._answerSet;
    }

    constructor(
        private fiDialogsService: FiDialogsService,
        private userService: UserService,
        private fiMediaStorageService: FiMediaStorageService,
        private fiFormHelperService: FiFormHelperService,
        private mediaViewerService: MediaViewerService,
        private formConditionHelperService: FiFormConditionHelperService,
        private userDataService: FiUserDataService,
    ) {}

    ngOnInit() {
        this.answersArray = this.getAnswersArray();
        if (!this.isCompleteReport) {
            // don't show print button when generate complete report pdf
            // https://chainformation.atlassian.net/browse/FI-5960
            this.fiFormHelperService.triggerIsPrintButtonShown(true);
        }
    }

    isPlainViewMode$(): Observable<boolean> {
        return this.fiFormHelperService.plainViewMode$;
    }

    isAnswerHidden(answer: IFormReportAnswer) {
        if (
            !this.formReport.Conditions.length ||
            !this.formConditionHelperService.isTargetQuestion(answer.Question, this.formReport.Conditions)
        )
            return false;

        return this.formConditionHelperService.isQuestionHidden(
            answer.Question,
            this.formReport.Questions,
            this.formReport.Conditions,
            this.answersArray,
        );
    }

    getSubmissionDate() {
        return this.answerSet.CreatedOn;
    }

    getSubmissionBusinessUnitName() {
        return this.answerSet.BusinessUnitName;
    }

    private getAnswersArray(): IFormReportAnswer[] {
        return this.answerSet.Answers;
    }

    showAnswerValue(answer: IFormReportAnswer) {
        return (
            answer.Question.Type !== FormQuestionType.Heading && answer.Question.Type !== FormQuestionType.TextMessage
        );
    }

    isAnswerShown(answer: IFormReportAnswer) {
        return (
            (!answer.Question.IsDisabled &&
                !this.isMediaBankElement(answer) &&
                !this.isAdditionalTextElement(answer)) ||
            (answer.Value && answer.Question.IsDisabled)
        );
    }

    isNumericOrTextElement(answer: IFormReportAnswer) {
        return this.fiFormHelperService.isNumericOrTextElement(answer.Question);
    }

    isSingleChoiceElement(answer: IFormReportAnswer) {
        return this.fiFormHelperService.isSingleChoiceQuestion(answer.Question);
    }

    isAdditionalTextElement(answer: IFormReportAnswer) {
        return this.fiFormHelperService.isAdditionalTextElement(answer.Question);
    }

    isMultipleChoiceElement(answer: IFormReportAnswer) {
        return this.fiFormHelperService.isMultipleChoiceQuestion(answer.Question);
    }

    isAttachmentElement(answer: IFormReportAnswer): boolean {
        return this.fiFormHelperService.isFileAttachmentElement(answer.Question);
    }

    isMediaBankElement(answer: IFormReportAnswer) {
        return this.fiFormHelperService.isMediaBankElement(answer.Question);
    }

    isMediaBankImage(answer: IFormReportAnswer) {
        return (
            answer.Question.MediaBankFileReferences.length &&
            answer.Question.MediaBankFileReferences[0].FileType === FileType.Image
        );
    }

    showBoldQuestionName(answer: IFormReportAnswer) {
        return answer.Question.Type !== FormQuestionType.TextMessage;
    }

    isQuestionPresetOptionsSelected(answer: IFormReportAnswer, presetOptions: IFormQuestionPresetOption) {
        return answer.ValuePresetOptionIds.indexOf(presetOptions.Id) !== -1;
    }

    goBack() {
        this.back.emit();
    }

    isAnonymousAnswers() {
        return this.fiFormHelperService.isAnonymousAnswers(this.formReport);
    }

    showUserCard() {
        // NOTE: this is service is located not is shared folder. it also has dependencies
        this.userService.openUserViewDialog(this.submission.Id);
    }

    isQuestionNotAnswered(answer: IFormReportAnswer) {
        return !answer.Value && !answer.MediaBankFileReferences.length;
    }

    getMediaBankFileReferencesForSelectedLanguage(question: IFormQuestion) {
        // just use preferred content language because report has no language selection
        const selectedLanguageId = this.userDataService.userData.PreferredContentLanguage;
        return question.MediaBankFileReferences.filter((mbRef) =>
            this.fiFormHelperService.isMediaBankFileReferenceForSelectedLanguage(mbRef, selectedLanguageId),
        );
    }

    ngOnDestroy() {
        if (!this.isCompleteReport) {
            this.fiFormHelperService.triggerIsPrintButtonShown(false);
        }
    }
}
